import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import HookFormTextField from 'src/components/hookform/HookFormTextField';
import BackButton from 'src/components/BackButton';
import Iconify from 'src/components/Iconify';
import SuccessButton from 'src/components/SuccessButton';
import WarningIcon from 'src/components/WarningIcon';
import { useEnvironment } from 'src/hooks/environment';
import { formatDate } from 'src/utils/formatTime';
import {
  getAppControlStandardRuleOptions,
  getAppControlStandardRuleWarning,
  getDefaultAppControlStandardRules,
} from 'src/utils/enums/uiOnly/appControlStandardRule';
import {
  BASE_POLICY_TEMPLATE,
  getBasePolicyTemplateOptions,
} from 'src/utils/enums/uiOnly/basePolicyTemplate';
import {
  getDefaultPolicyRuleOptions,
  getPolicyRuleOptionOptions,
  getPolicyRuleOptionWarning,
} from 'src/utils/enums/uiOnly/policyRuleOption';

export const DETAILS_STEP = 'details';
export const RULE_OPTIONS_STEP = 'ruleOptions';

const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FIRST_POLICY_VERSION = '1.0.0.0';

const DETAILS_FIELDS = ['policyName', 'policyId', 'portalOptions'];

const TEMPLATE_OPTIONS = getBasePolicyTemplateOptions();
const STANDARD_RULE_OPTIONS = getAppControlStandardRuleOptions();
const RULE_OPTIONS = getPolicyRuleOptionOptions();

const SECTION_TITLE_SX = { color: '#F3F4F6', fontWeight: 600, fontSize: '0.88rem' };
const CAPTION_SX = { color: '#9CA3AF' };

const schema = Yup.object().shape({
  policyName: Yup.string().trim().required('Policy name is required'),
  policyId: Yup.string()
    .trim()
    .required('Base policy ID is required')
    .matches(GUID_REGEX, 'Must be a valid GUID: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'),
  portalOptions: Yup.object().shape({
    policyInfoId: Yup.string().trim().required('Policy info ID is required'),
    template: Yup.string().oneOf(Object.values(BASE_POLICY_TEMPLATE)).required(),
  }),
  ruleOptions: Yup.array().of(Yup.number()),
});

const newPolicyId = () => crypto.randomUUID().toUpperCase();
const suggestPolicyName = environmentName => `AppControl.ai - BasePolicy - ${environmentName}`;

const todaysPolicyInfoId = () => formatDate(new Date(), 'yyyy-MM-dd');

// The form's values are the request's shape: a new policy starts from our defaults, and a new
// version from the last one's choices with the policy info ID moved on to today, like the version.
const buildDefaultValues = (environmentName, nextVersion) =>
  nextVersion
    ? {
        policyName: nextVersion.policyName,
        policyId: nextVersion.policyId,
        portalOptions: { ...nextVersion.portalOptions, policyInfoId: todaysPolicyInfoId() },
        ruleOptions: nextVersion.ruleOptions,
      }
    : {
        policyName: environmentName ? suggestPolicyName(environmentName) : '',
        policyId: newPolicyId(),
        portalOptions: {
          template: BASE_POLICY_TEMPLATE.AllowMicrosoft,
          policyInfoId: todaysPolicyInfoId(),
          ...getDefaultAppControlStandardRules(),
        },
        ruleOptions: getDefaultPolicyRuleOptions(),
      };

// One option's checkbox, with a short description beneath its label and, when the choice makes that
// option risky, a warning tooltip beside it.
const OptionCheckbox = ({ option, checked, onToggle, label, warning }) => (
  <FormControlLabel
    sx={{ alignItems: 'flex-start', m: 0, gap: 1 }}
    control={<Checkbox size="small" checked={checked} onChange={onToggle} sx={{ py: 0.25 }} />}
    label={
      <Stack spacing={0.25}>
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Typography variant="body2" sx={{ color: '#F3F4F6' }}>
            {label ?? option.label}
          </Typography>
          <WarningIcon message={warning} />
        </Stack>
        <Typography variant="caption" sx={CAPTION_SX}>
          {option.description}
          {option.emphasis ? (
            <Box component="span" sx={{ fontWeight: 700, color: '#D1D5DB' }}>
              {` ${option.emphasis}`}
            </Box>
          ) : null}
        </Typography>
      </Stack>
    }
  />
);

// The standard rules are a field each in the portal options.
const StandardRuleFields = ({ control }) => (
  <Stack gap={1}>
    {STANDARD_RULE_OPTIONS.map(option => (
      <Controller
        key={option.value}
        name={`portalOptions.${option.value}`}
        control={control}
        render={({ field: { value, onChange } }) => (
          <OptionCheckbox
            option={option}
            checked={!!value}
            onToggle={() => onChange(!value)}
            warning={getAppControlStandardRuleWarning(option.value, !!value)}
          />
        )}
      />
    ))}
  </Stack>
);

const renderRuleOptionLabel = option => (
  <>
    <Box component="span" sx={{ color: '#9CA3AF', display: 'inline-block', minWidth: 24 }}>
      {option.value}
    </Box>
    {option.label}
  </>
);

// The rule options are one array of Microsoft's option numbers.
const RuleOptionFields = ({ control }) => (
  <Controller
    name="ruleOptions"
    control={control}
    render={({ field: { value, onChange } }) => (
      <Stack gap={1}>
        {RULE_OPTIONS.map(option => {
          const checked = value.includes(option.value);
          return (
            <OptionCheckbox
              key={option.value}
              option={option}
              checked={checked}
              onToggle={() =>
                onChange(
                  checked ? value.filter(item => item !== option.value) : [...value, option.value],
                )
              }
              label={renderRuleOptionLabel(option)}
              warning={getPolicyRuleOptionWarning(option.value, value)}
            />
          );
        })}
      </Stack>
    )}
  />
);

// The wizard behind a managed base policy: the policy's details, template and our standard rules
// first, then its rule options. One form spans both steps, so going back keeps what was entered.
// Given the next version of an existing policy, it versions that policy instead, whose ID and
// template stay as they are.
const ManagedBasePolicyForm = ({
  step,
  onStepChange,
  loading,
  onCancel,
  onSubmit,
  nextVersion,
}) => {
  const { data: environment } = useEnvironment();
  const environmentName = environment?.name || '';
  const [defaultValues] = useState(() => buildDefaultValues(environmentName, nextVersion));
  const isNewVersion = !!nextVersion;

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    getValues,
    formState: { isValid },
  } = formMethods;

  // The environment can arrive after the form opens, in which case the name is suggested then.
  useEffect(() => {
    if (environmentName && !getValues('policyName')) {
      setValue('policyName', suggestPolicyName(environmentName), { shouldValidate: true });
    }
  }, [environmentName, getValues, setValue]);

  const continueToRuleOptions = async () => {
    if (await trigger(DETAILS_FIELDS)) {
      onStepChange(RULE_OPTIONS_STEP);
    }
  };

  const submit = ({ policyName, policyId, portalOptions, ruleOptions }) => {
    const payload = {
      policyName: policyName.trim(),
      portalOptions: { ...portalOptions, policyInfoId: portalOptions.policyInfoId.trim() },
      ruleOptions: [...ruleOptions].sort((a, b) => a - b),
    };

    onSubmit(isNewVersion ? payload : { ...payload, policyId: policyId.trim() });
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit(submit)}>
      {step === DETAILS_STEP ? (
        <Stack gap={2}>
          <HookFormTextField
            name="policyName"
            label="Policy name"
            formMethods={formMethods}
            size="small"
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              label="Version (automatic)"
              value={nextVersion?.nextPolicyVersion ?? FIRST_POLICY_VERSION}
              size="small"
              fullWidth
              disabled
            />
            <HookFormTextField
              name="portalOptions.policyInfoId"
              label="Policy info ID"
              formMethods={formMethods}
              size="small"
              fullWidth
            />
          </Stack>
          <Stack direction="row" gap={1} alignItems="flex-start">
            <HookFormTextField
              name="policyId"
              label="Base policy ID"
              formMethods={formMethods}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              size="small"
              fullWidth
              disabled={isNewVersion}
            />
            {isNewVersion ? null : (
              <Tooltip title="Generate a new ID">
                <IconButton
                  onClick={() =>
                    setValue('policyId', newPolicyId(), { shouldDirty: true, shouldValidate: true })
                  }
                  sx={{
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    color: '#D1D5DB',
                    // Matches the outline of the text field beside it.
                    border: '1px solid rgba(255, 255, 255, 0.23)',
                    '&:hover': { borderColor: 'rgba(255, 255, 255, 0.87)' },
                  }}>
                  <Iconify icon="mdi:refresh" width={18} height={18} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <Divider sx={{ borderColor: '#5B5B5B' }} />

          <Stack gap={0.75}>
            <Typography sx={SECTION_TITLE_SX}>Template</Typography>
            <Controller
              name="portalOptions.template"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field} sx={{ gap: 1 }}>
                  {TEMPLATE_OPTIONS.map(option => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      disabled={isNewVersion}
                      sx={{ alignItems: 'flex-start', m: 0, gap: 1 }}
                      control={<Radio size="small" sx={{ py: 0.25 }} />}
                      label={
                        <Stack spacing={0.25}>
                          <Typography variant="body2" sx={{ color: '#F3F4F6' }}>
                            {option.label}
                          </Typography>
                          <Typography variant="caption" sx={CAPTION_SX}>
                            {option.description}
                          </Typography>
                        </Stack>
                      }
                    />
                  ))}
                </RadioGroup>
              )}
            />
          </Stack>

          <Stack gap={0.75}>
            <Typography sx={SECTION_TITLE_SX}>AppControl.ai standard rules</Typography>
            <StandardRuleFields control={control} />
          </Stack>

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <BackButton onClick={onCancel}>Cancel</BackButton>
            <SuccessButton type="button" onClick={continueToRuleOptions}>
              Continue
            </SuccessButton>
          </Stack>
        </Stack>
      ) : (
        <Stack gap={2}>
          <RuleOptionFields control={control} />

          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <BackButton disabled={loading} onClick={() => onStepChange(DETAILS_STEP)}>
              Back
            </BackButton>
            <SuccessButton type="submit" loading={loading} disabled={loading || !isValid}>
              {isNewVersion ? 'Update' : 'Create'}
            </SuccessButton>
          </Stack>
        </Stack>
      )}
    </form>
  );
};

ManagedBasePolicyForm.propTypes = {
  step: PropTypes.oneOf([DETAILS_STEP, RULE_OPTIONS_STEP]).isRequired,
  onStepChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  nextVersion: PropTypes.shape({
    policyId: PropTypes.string.isRequired,
    policyName: PropTypes.string.isRequired,
    nextPolicyVersion: PropTypes.string.isRequired,
    portalOptions: PropTypes.object.isRequired,
    ruleOptions: PropTypes.arrayOf(PropTypes.number).isRequired,
  }),
};

export default ManagedBasePolicyForm;
